import React from "react"
import { useSnackbar, SnackbarProvider, SnackbarOrigin } from "notistack"

export interface NotificationContextValue {
    notifyError: (message: string) => void
    notifySuccess: (message: string) => void
    notifyInfo: (message: string) => void
}
export const NotificationContext = React.createContext<NotificationContextValue>({
    notifyError: (message: string) => {},
    notifySuccess: (message: string) => {},
    notifyInfo: (message: string) => {}
})
interface NotificationProviderProps {
    value?: NotificationContextValue
    children: React.ReactNode
}

const messageNotLoginRelated = (mgs: string) => !mgs.includes("Login required")

const BaseNotificationProvider: React.FunctionComponent<NotificationProviderProps> = props => {
    const { enqueueSnackbar } = useSnackbar()

    const notifyError = (message: string) => {
        if (messageNotLoginRelated(message)) enqueueSnackbar(message, { variant: "error" })
    }

    const notifySuccess = (message: string) => {
        enqueueSnackbar(message, { variant: "success" })
    }

    const notifyInfo = (message: string) => {
        enqueueSnackbar(message, { variant: "info" })
    }

    const authContextValue = {
        notifyError,
        notifySuccess,
        notifyInfo
    }

    return <NotificationContext.Provider value={props.value ?? authContextValue} {...props} />
}

export const NotificationProvider = (props: NotificationProviderProps) => {
    const anchorOrigin: SnackbarOrigin = { vertical: "top", horizontal: "right" }
    return (
        <SnackbarProvider anchorOrigin={anchorOrigin}>
            <BaseNotificationProvider {...props} />
        </SnackbarProvider>
    )
}

export const useNotification = () => {
    const context = React.useContext(NotificationContext)
    if (!context) throw new Error("useNotification must be used within a NotificationProvider")
    return context
}
